import { useEffect, useState, useMemo } from 'react';
import { HelpCircle, Play, Pause, Search, Loader2 } from 'lucide-react';
import { Modal } from '../ui';
import { getActiveFaq } from '../../services/faqService';
import { useGlobalAudio } from '../../context/GlobalAudioContext';
import FaqFreeQuestion from './FaqFreeQuestion';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

function parseJson(str) {
    if (!str) return {};
    if (typeof str === 'object') return str;
    try { return JSON.parse(str); } catch { return {}; }
}

const FaqPanel = ({ isOpen, onClose }) => {
    const { playAudio, stopAudio, currentUrl, isPlaying } = useGlobalAudio();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState(null);
    const lang = 'fr';

    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            try {
                setLoading(true);
                const data = await getActiveFaq(lang);
                setItems(data);
            } catch (err) {
                console.error('[FaqPanel] load error', err);
            } finally {
                setLoading(false);
            }
        })();
        return () => stopAudio();
    }, [isOpen, stopAudio]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter(item => {
            const question = (parseJson(item.questionbylang)[lang] || '').toLowerCase();
            const answer = (parseJson(item.answerbylang)[lang] || '').toLowerCase();
            const keywords = (item.keywords || '').toLowerCase();
            return question.includes(q) || answer.includes(q) || keywords.includes(q);
        });
    }, [items, search]);

    // Grouper par catégorie
    const grouped = useMemo(() => {
        const map = {};
        filtered.forEach(item => {
            const cat = item.category || 'Général';
            if (!map[cat]) map[cat] = [];
            map[cat].push(item);
        });
        return map;
    }, [filtered]);

    const toggleAudio = (item) => {
        const audioPath = parseJson(item.audiobylang)[lang];
        if (!audioPath) return;
        const fullUrl = `${FILES_BASE_URL}/${audioPath}`;
        if (isPlaying && currentUrl === fullUrl) {
            stopAudio();
        } else {
            playAudio(fullUrl, { anchor: 'bottom-right' });
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Questions fréquentes"
            icon={HelpCircle}
            hideFooter={true}
            size="lg"
        >
            <div className="space-y-4">
                {/* Zone question libre */}
                <FaqFreeQuestion />

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Rechercher dans la FAQ…"
                        className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                    </div>
                ) : Object.keys(grouped).length === 0 ? (
                    <p className="text-gray-500 text-center py-6 text-sm">
                        {search ? 'Aucun résultat.' : 'Aucune FAQ disponible.'}
                    </p>
                ) : (
                    <div className="space-y-4">
                        {Object.entries(grouped).map(([cat, list]) => (
                            <div key={cat}>
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">{cat}</h3>
                                <div className="space-y-2">
                                    {list.map(item => {
                                        const question = parseJson(item.questionbylang)[lang] || '(sans question)';
                                        const answer = parseJson(item.answerbylang)[lang] || '';
                                        const audioPath = parseJson(item.audiobylang)[lang];
                                        const fullAudioUrl = audioPath ? `${FILES_BASE_URL}/${audioPath}` : null;
                                        const isThisPlaying = isPlaying && currentUrl === fullAudioUrl;
                                        const isExpanded = expanded === item.uuid;
                                        return (
                                            <div key={item.uuid} className="border rounded-lg overflow-hidden">
                                                <button
                                                    onClick={() => setExpanded(isExpanded ? null : item.uuid)}
                                                    className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                                                >
                                                    <span className="font-medium text-gray-900 text-sm">{question}</span>
                                                    <span className="text-gray-400 text-xs">{isExpanded ? '−' : '+'}</span>
                                                </button>
                                                {isExpanded && (
                                                    <div className="px-4 py-3 border-t bg-gray-50">
                                                        <div
                                                            className="text-sm text-gray-700 prose prose-sm max-w-none"
                                                            dangerouslySetInnerHTML={{ __html: answer }}
                                                        />
                                                        {audioPath && (
                                                            <button
                                                                onClick={() => toggleAudio(item)}
                                                                className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-xs"
                                                            >
                                                                {isThisPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                                                                {isThisPlaying ? 'Pause' : 'Écouter'}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default FaqPanel;
